import React, { useEffect, useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { RetellWebClient } from 'retell-client-js-sdk';
import { startRetellCall } from '../api/retell/start';

declare global {
  interface Window {
    initializeYobiAiWidget: (config: any) => void;
  }
}

const rainbowAnimation = keyframes`
  0% { border-color: #ff0000; }
  17% { border-color: #ff8000; }
  33% { border-color: #ffff00; }
  50% { border-color: #00ff00; }
  67% { border-color: #0000ff; }
  83% { border-color: #8000ff; }
  100% { border-color: #ff0000; }
`;

const WidgetContainer = styled.div`
  position: fixed;
  top: 80px; /* Position below navbar */
  right: 20px;
  z-index: 1000;
  transition: all 0.3s ease;
`;

const WidgetButton = styled.button<{ isRecording: boolean }>`
  background: white;
  color: #007bff;
  border: 2px solid;
  border-radius: 25px;
  padding: 8px 16px;
  min-width: 120px;
  height: 40px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  font-weight: 500;
  animation: ${rainbowAnimation} 3s linear infinite;
  white-space: nowrap;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    animation: none;
  }

  ${props => props.isRecording && `
    background: #ef3b44;
    color: white;
    animation: none;
  `}
`;

interface WidgetContentProps {
  isExpanded: boolean;
}

const WidgetContent = styled.div<WidgetContentProps>`
  position: absolute;
  top: 0;
  right: 60px;
  width: 350px;
  height: 500px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  opacity: ${(props: WidgetContentProps) => props.isExpanded ? 1 : 0};
  visibility: ${(props: WidgetContentProps) => props.isExpanded ? 'visible' : 'hidden'};
  transform: ${(props: WidgetContentProps) => props.isExpanded ? 'translateX(0)' : 'translateX(20px)'};
`;

const YobiAIWidget: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const retellClientRef = useRef<RetellWebClient | null>(null);
  const initializationTimeoutRef = useRef<NodeJS.Timeout>();

  const startCall = async () => {
    if (isInitializing || isRecording) return;

    setIsInitializing(true);
    setError(null);

    try {
      const data = await startRetellCall();

      // Initialize Retell Web Client
      const retellClient = new RetellWebClient();
      retellClientRef.current = retellClient;

      // Set up event listeners
      retellClient.on('call_started', () => {
        console.log('Call started');
        setIsRecording(true);
        setIsInitializing(false);
      });

      retellClient.on('call_ended', () => {
        console.log('Call ended');
        setIsRecording(false);
        retellClient.stopCall();
      });

      retellClient.on('error', (error: { message: string }) => {
        console.error('Retell error:', error);
        setError(error.message || 'An error occurred during the call');
        setIsRecording(false);
        setIsInitializing(false);
        retellClient.stopCall();
      });

      // Start the call
      await retellClient.startCall({
        accessToken: data.accessToken,
        sampleRate: 24000,
      });

    } catch (error: any) {
      setError(error.message);
      setIsRecording(false);
      setIsInitializing(false);
    }
  };

  const stopCall = () => {
    if (retellClientRef.current) {
      retellClientRef.current.stopCall();
      retellClientRef.current.removeAllListeners();
      retellClientRef.current = null;
    }
    setIsRecording(false);
    setError(null);
  };

  useEffect(() => {
    return () => {
      if (initializationTimeoutRef.current) {
        clearTimeout(initializationTimeoutRef.current);
      }
      if (retellClientRef.current) {
        retellClientRef.current.stopCall();
        retellClientRef.current.removeAllListeners();
        retellClientRef.current = null;
      }
    };
  }, []);

  return (
    <WidgetContainer>
      <WidgetButton 
        onClick={isRecording ? stopCall : startCall}
        isRecording={isRecording}
        disabled={isInitializing}
      >
        {isInitializing ? '...' : isRecording ? '×' : (
          <>
            Help me, Yobi One!
            <br/>
            (Click for help)
          </>
        )}
      </WidgetButton>
      <WidgetContent isExpanded={isExpanded}>
        <div id="yobi_ai_widget"></div>
      </WidgetContent>
    </WidgetContainer>
  );
};

export default YobiAIWidget; 